<template>
    <div class="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <TopNav @toggle-sidebar="toggleSidebar" />
      <SideMenu :isOpen="isSidebarOpen" @close-sidebar="isSidebarOpen = false" />
  
      <main class="p-6 md:ml-64 h-auto pt-20 flex justify-center">
        <section class="w-full max-w-lg dark:bg-gray-800 rounded-lg p-8">
          <div class="mb-6 flex justify-between items-center">
            <h2 class="text-3xl font-semibold text-gray-800 dark:text-gray-100">Create Instance User</h2>
            <router-link :to="$routeWithBranch('ViewInstance', { params: { id: instanceId } })"
              class="mt-4 ml-4 flex items-center text-primary-700 dark:text-primary-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span class="ml-2">Back</span>
            </router-link>
          </div>
  
          <form @submit.prevent="createInstanceUser">
            <div v-if="errors.length" class="mb-4 bg-red-100 text-red-800 p-3 rounded">
              <ul>
                <li v-for="(error, index) in errors" :key="index" class="text-sm">{{ error }}</li>
              </ul>
            </div>
  
            <!-- Name -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">First Name</label>
                <input type="text" v-model="form.first_name" class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600" required />
              </div>
              <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Last Name</label>
                <input type="text" v-model="form.last_name" class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600" required />
              </div>
            </div>
  
            <!-- Email & Password -->
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Email</label>
              <input type="email" v-model="form.email" class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600" required />
            </div>
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Password</label>
              <input type="password" v-model="form.password" class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600" />
              <small class="text-xs text-gray-500 dark:text-gray-400">Optional. If omitted, a secure password will be generated.</small>
            </div>
  
            <!-- Role -->
            <div class="mb-4">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Role</label>
              <select v-model="form.role" class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600" required>
                <option disabled value="">Select a role</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="learner">Learner</option>
                <option value="instructor">Instructor</option>
              </select>
            </div>
  
            <!-- Flags -->
            <div class="space-y-3 mb-4">
              <div class="flex items-center">
                <input type="checkbox" v-model="form.is_active" id="is_active" class="h-4 w-4 text-blue-600 rounded dark:border-gray-600" />
                <label for="is_active" class="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</label>
              </div>
              <div class="flex items-center">
                <input type="checkbox" v-model="form.primary_access" id="primary_access" class="h-4 w-4 text-blue-600 rounded dark:border-gray-600" />
                <label for="primary_access" class="ml-2 text-sm text-gray-700 dark:text-gray-300">Primary Instance Access</label>
              </div>
              <div class="flex items-center">
                <input type="checkbox" v-model="form.is_owner" id="is_owner" class="h-4 w-4 text-blue-600 rounded dark:border-gray-600" />
                <label for="is_owner" class="ml-2 text-sm text-gray-700 dark:text-gray-300">Mark as Instance Owner</label>
              </div>
              <div class="flex items-center">
                <input type="checkbox" v-model="form.skip_email" id="skip_email" class="h-4 w-4 text-blue-600 rounded dark:border-gray-600" />
                <label for="skip_email" class="ml-2 text-sm text-gray-700 dark:text-gray-300">Skip Welcome Email</label>
              </div>
            </div>
  
            <!-- Submit -->
            <div class="mt-6">
              <button type="submit" :disabled="isSubmitting"
                class="w-full !bg-secondary-600 text-white font-semibold py-3 rounded-lg hover:!bg-secondary-700 flex items-center justify-center">
                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                {{ isSubmitting ? 'Creating...' : 'Create User' }}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  </template>
  
  <script>
  import TopNav from '@/components/headers/TopNav.vue';
  import SideMenu from '@/components/SideMenu.vue';
  import axios from 'axios';
  
  export default {
    components: { TopNav, SideMenu },
    props: ['instance_id'],
    data() {
      return {
        form: {
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role: '',
          primary_access: true,
          is_owner: false,
          is_active: true,
          skip_email: false,
        },
        isSubmitting: false,
        errors: [],
        isSidebarOpen: false,
      };
    },
    computed: {
      instanceId() {
        return parseInt(this.instance_id);
      }
    },
    methods: {
      toggleSidebar() {
        this.isSidebarOpen = !this.isSidebarOpen;
      },
      async createInstanceUser() {
        this.isSubmitting = true;
        this.errors = [];
  
        try {
          const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
            query: `
              mutation CreateInstanceUser(
                $instance_id: Int!
                $first_name: String!
                $last_name: String!
                $email: String!
                $password: String
                $role: String!
                $primary_access: Boolean
                $is_active: Boolean
                $is_owner: Boolean
                $skip_email: Boolean
              ) {
                createInstanceUser(
                  instance_id: $instance_id
                  first_name: $first_name
                  last_name: $last_name
                  email: $email
                  password: $password
                  role: $role
                  primary_access: $primary_access
                  is_active: $is_active
                  is_owner: $is_owner
                  skip_email: $skip_email
                ) {
                  id
                  first_name
                  last_name
                  email
                }
              }
            `,
            variables: {
              ...this.form,
              instance_id: this.instanceId,
            },
          });
  
          if (response.data.errors) {
            this.errors = response.data.errors.map(e => e.message);
          } else {
            this.$routerPushWithBranch('ViewInstance', { params: { id: this.instanceId } });
          }
        } catch (error) {
          console.error('Error:', error);
          this.errors.push('An unexpected error occurred.');
        } finally {
          this.isSubmitting = false;
        }
      }
    },
  };
  </script>
  